import React from 'react';
import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import { postData } from '../../Api';

const EMPTY_BODY = '{"type":"quill","document":{}}';

const SCOPED_CSS = `
.taroff-task-form .ql-editor { direction: rtl; text-align: right; min-height: 140px; }
.taroff-task-form .ql-editor.ql-blank::before { left: auto; right: 15px; }
`;

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
            {label}
            {hint && <span className="mr-1 text-gray-400 font-normal">{hint}</span>}
        </span>
        {children}
    </label>
);

const inputClass = 'w-full rounded-lg border border-gray-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400';

export default function TaroffTaskForm(props) {
    const [form, setForm] = useState({
        title: '',
        description: EMPTY_BODY,
        assignee_id: '',
        type_id: '',
        priority: 2,
        due_date: '',
    });
    const [options, setOptions] = useState({ admins: [], types: [], priorities: [] });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        postData('/api/panel/page/taroff-tasks/form-data').then((response) => {
            if (response.status !== 'success') return;
            setOptions(response.data);
            // Default to the first type so a task is never typeless by accident.
            setForm((f) => ({ ...f, type_id: f.type_id || response.data.types?.[0]?.id || '' }));
        });
    }, []);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const handleSave = async () => {
        if (!form.title.trim()) {
            setError('عنوان کار را وارد کنید');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const response = await postData('/api/panel/page/taroff-task/save', {
                title: form.title,
                description: form.description,
                assignee_id: form.assignee_id ? parseInt(form.assignee_id) : null,
                type_id: form.type_id ? parseInt(form.type_id) : null,
                priority: parseInt(form.priority) || 2,
                due_date: form.due_date || null,
            });
            if (response.status === 'success') {
                props.onSaved ? props.onSaved(response.data?.id) : props.onDismiss?.();
            } else {
                setError(response.message || 'خطا در ثبت کار');
            }
        } catch (e) {
            setError('خطا در برقراری ارتباط با سرور');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div dir="rtl" className="taroff-task-form font-iransans text-gray-900 dark:text-gray-100">
            <style>{SCOPED_CSS}</style>

            <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold">
                    <i className="far fa-list-check ml-2 text-indigo-500" />
                    کار جدید
                </h2>
                <button onClick={props.onDismiss} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700" title="بستن">
                    <i className="fas fa-xmark fa-lg" />
                </button>
            </div>

            <div className="space-y-3.5">
                <Field label="عنوان">
                    <input
                        className={inputClass}
                        value={form.title}
                        onChange={(e) => update({ title: e.target.value })}
                        placeholder="مثلاً: بررسی مغایرت تسویه فروشگاه ..."
                        autoFocus
                    />
                </Field>

                <Field label="شرح">
                    <ReactQuill
                        theme="snow"
                        value={(() => { try { return JSON.parse(form.description).document; } catch (e) { return form.description; } })()}
                        onChange={(content, delta, source, editor) =>
                            update({ description: JSON.stringify({ type: 'quill', document: editor.getContents() }) })}
                        placeholder="توضیح کار، لینک‌ها و هر چیزی که طرف مقابل باید بداند..."
                    />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <Field label="مسئول" hint="(اختیاری)">
                        <select className={inputClass} value={form.assignee_id} onChange={(e) => update({ assignee_id: e.target.value })}>
                            <option value="">تعیین نشده — هرکسی می‌تواند بردارد</option>
                            {options.admins?.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                        </select>
                    </Field>

                    <Field label="نوع">
                        <select className={inputClass} value={form.type_id} onChange={(e) => update({ type_id: e.target.value })}>
                            <option value="">—</option>
                            {options.types?.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                        </select>
                    </Field>

                    <Field label="اولویت">
                        <select className={inputClass} value={form.priority} onChange={(e) => update({ priority: e.target.value })}>
                            {options.priorities?.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                        </select>
                    </Field>

                    <Field label="مهلت" hint="(اختیاری — ۱۴۰۴/۰۵/۲۱)">
                        <input
                            className={inputClass + ' text-left'}
                            dir="ltr"
                            value={form.due_date}
                            onChange={(e) => update({ due_date: e.target.value })}
                            placeholder="1404/05/21"
                        />
                    </Field>
                </div>

                {error && <div className="text-red-600 text-xs">{error}</div>}

                <div className="flex items-center justify-end gap-2 pt-1">
                    <button onClick={props.onDismiss} className="px-4 py-2 rounded-lg text-sm bg-gray-100 dark:bg-zinc-700 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors">
                        انصراف
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-40 transition-colors"
                    >
                        <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-plus'} />
                        ثبت کار
                    </button>
                </div>
            </div>
        </div>
    );
}
